/**
 * Recursively remove editor-only fields from a payload config before sending
 * it to the backend. Apple's JSON schemas use `additionalProperties: false`
 * for most payloads, so any client-only bookkeeping (drag handles, resolved
 * icon URLs, deployment status flags, etc.) must be dropped or `/validate`
 * and policy create will reject the request.
 *
 * Convention: editor-only fields are prefixed with `_` (e.g. `_id`,
 * `_resolvedIconUrl`). This helper drops any such keys at every depth.
 *
 * It also drops a small allow-list of known historical editor leaks that do
 * NOT use the underscore convention but have caused validation failures in
 * the past — currently `Icon` and `IconKind` on WebClip entries inside the
 * home screen layout payload. (Home-screen-specific stripping still happens
 * in `homescreen/projection.ts`; this is a defensive second pass.)
 */
const KNOWN_EDITOR_LEAKS = new Set(["IconKind"]);

export function stripEditorOnlyFields<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((v) => stripEditorOnlyFields(v)) as unknown as T;
  }
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (k.startsWith("_")) continue;
      if (KNOWN_EDITOR_LEAKS.has(k)) continue;
      out[k] = stripEditorOnlyFields(v);
    }
    return out as unknown as T;
  }
  return value;
}
